// =============================================================================
// Product/Inventory detail — faithful port of POS frmProductDashboard /
// IMS Inventory_View. Shows full identity, pricing, quantities, plant taxonomy,
// source, and status. Edit via ProductForm.
// =============================================================================
import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Pencil, Tag, Image, Gavel, BarChart3, X, PackageMinus, Trash2 } from 'lucide-react'
import {
  getProduct, productPriceBreakdown, type ProductPriceBreakdownRow,
  listScrappedInventory, addScrappedInventory, deleteScrappedInventory,
} from '../services/data'
import { useAuth } from '../context/AuthContext'
import { PageHeader, Card, Spinner, Button, StatCard } from '../components/shared/ui'
import { currency, productCode, date } from '../lib/format'
import { printHtml } from '../lib/print'
import { toast } from 'react-toastify'
import type { ScrappedInventory } from '../types'
import { printProductTags, type TagSize } from '../lib/tags'
import { THOMSONS_LOGO } from '../lib/assets'
import ProductForm from '../components/ProductForm'
import type { Product } from '../types'

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-gray-50 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 text-right">{value}</span>
    </div>
  )
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [tagOpen, setTagOpen] = useState(false)
  const [salesOpen, setSalesOpen] = useState(false)
  const [scrapOpen, setScrapOpen] = useState(false)

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    getProduct(id).then(setProduct).finally(() => setLoading(false))
  }, [id])

  useEffect(() => { load() }, [load])

  if (loading) return <Spinner label="Loading product…" />
  if (!product) return <div className="text-gray-500">Product not found.</div>

  const taxonomy = [product.genus, product.species, product.variety].filter(Boolean).join(' ')
  const care = [
    product.height && `Height: ${product.height}`,
    product.bloom && `Bloom: ${product.bloom}`,
    product.exposure && `Exposure: ${product.exposure}`,
    product.fruit && `Fruit: ${product.fruit}`,
  ].filter(Boolean).join(' &nbsp;•&nbsp; ')
  const price = product.sale_price ?? product.retail_price ?? 0

  // Ports of InventoryTag_Report / InventorySign_Report / AuctionSign_Report.
  // Scannable barcode tags (legacy PrintBarcode) are handled by TagPrintDialog.
  const printSign = () => printHtml(`Sign — ${product.name}`, `
    <div style="text-align:center;border:3px solid #0a3a0a;border-radius:12px;padding:32px;max-width:640px;margin:auto">
      <img src="${THOMSONS_LOGO}" style="height:64px;width:auto;object-fit:contain;margin-bottom:8px" alt="Thomson's"/><br/>
      ${product.image_url ? `<img src="${product.image_url}" style="max-height:280px;max-width:100%;object-fit:contain"/><br/>` : ''}
      <div style="font-size:34px;font-weight:bold;margin:12px 0">${product.name}</div>
      ${taxonomy ? `<div style="font-size:20px;font-style:italic;color:#333">${taxonomy}</div>` : ''}
      ${care ? `<div style="font-size:15px;color:#444;margin:12px 0">${care}</div>` : ''}
      <div style="font-size:40px;font-weight:bold;color:#0a3a0a;margin-top:12px">${currency(price)}</div>
    </div>`)
  const printAuction = () => printHtml(`Auction Sign — ${product.name}`, `
    <div style="text-align:center;border:4px dashed #111;padding:40px;max-width:700px;margin:auto">
      <img src="${THOMSONS_LOGO}" style="height:56px;width:auto;object-fit:contain;margin-bottom:8px" alt="Thomson's"/>
      <div style="font-size:18px;letter-spacing:2px;color:#666">AUCTION</div>
      <div style="font-size:44px;font-weight:bold;margin:16px 0">${product.name}</div>
      ${taxonomy ? `<div style="font-size:22px;font-style:italic">${taxonomy}</div>` : ''}
      ${product.size ? `<div style="font-size:20px;margin-top:8px">Size: ${product.size}</div>` : ''}
      <div style="font-size:22px;color:#444;margin-top:16px">Lot Code: ${productCode(product)}</div>
    </div>`)

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate('/products')} className="mb-4">
        <ArrowLeft size={16} /> Back to Products
      </Button>
      <PageHeader
        title={product.name}
        subtitle={[productCode(product), product.inventory_type].filter(v => v && v !== '—').join(' · ') || undefined}
        actions={
          <>
            <Button variant="secondary" onClick={() => setTagOpen(true)}><Tag size={16} /> Print Tag</Button>
            <Button variant="secondary" onClick={printSign}><Image size={16} /> Print Sign</Button>
            <Button variant="secondary" onClick={printAuction}><Gavel size={16} /> Auction Sign</Button>
            <Button variant="secondary" onClick={() => setSalesOpen(true)}><BarChart3 size={16} /> Sales Analysis</Button>
            <Button variant="secondary" onClick={() => setScrapOpen(true)}><PackageMinus size={16} /> Scrap / Loss</Button>
            <Button onClick={() => setEditing(true)}><Pencil size={16} /> Edit</Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Retail" value={currency(product.retail_price ?? 0)} accent="green" />
        <StatCard label="Sale" value={currency(product.sale_price ?? product.retail_price ?? 0)} accent="blue" />
        <StatCard label="Cost" value={currency(product.cost_price ?? 0)} accent="amber" />
        <StatCard label="On Hand" value={product.qty_on_hand ?? product.qty_remaining ?? 0} accent="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Identity</h3>
          <Row label="Description" value={product.name} />
          <Row label="Alt Description" value={product.alt_description} />
          <Row label="Inventory Code" value={product.legacy_pos_inventory_code ?? product.legacy_ims_inventory_code} />
          <Row label="SKU / Item #" value={product.sku} />
          <Row label="Scan / Barcode" value={product.scan_number} />
          <Row label="Type" value={product.inventory_type} />
          <Row label="Taxonomy" value={taxonomy} />
          <Row label="Size / Unit" value={[product.size, product.unit].filter(Boolean).join(' / ')} />
          <Row label="Color" value={product.color} />
          <Row label="Supplier" value={product.supplier_name} />
          <Row label="Inventory Year" value={product.inventory_year} />
          <div className="mt-3 flex flex-wrap gap-2">
            <span className={`px-2 py-0.5 rounded text-xs ${product.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{product.is_active ? 'Active' : 'Inactive'}</span>
            {product.is_depleted && <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Depleted</span>}
            {product.in_price_book && <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Price Book</span>}
            {product.legacy_ims_inventory_id ? <span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">IMS</span> : null}
            {product.legacy_pos_inventory_id ? <span className="px-2 py-0.5 rounded text-xs bg-green-50 text-green-600">POS</span> : null}
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Pricing</h3>
            <Row label="Cost" value={currency(product.cost_price)} />
            <Row label="Retail" value={currency(product.retail_price)} />
            <Row label="Sale" value={currency(product.sale_price)} />
            <Row label="Wholesale" value={currency(product.wholesale_price)} />
            <Row label="Tax %" value={product.tax_percent != null ? `${product.tax_percent}%` : null} />
            <Row label="Freight" value={product.freight != null ? currency(product.freight) : null} />
            <Row label="Actual Cost / Plant" value={product.actual_cost_per_plant != null ? currency(product.actual_cost_per_plant) : null} />
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Quantities</h3>
            <Row label="On Hand" value={product.qty_on_hand} />
            <Row label="Remaining" value={product.qty_remaining} />
            <Row label="Ordered" value={product.qty_ordered} />
            <Row label="Received" value={product.qty_received} />
            <Row label="Used (jobs)" value={product.qty_used_jobs} />
            <Row label="Scrapped" value={product.qty_scrapped} />
            <Row label="Sold (retail)" value={product.qty_sold_retail} />
          </Card>
        </div>
      </div>

      {product.notes && (
        <Card className="p-5 mt-6">
          <h3 className="font-semibold text-gray-800 mb-2">Notes</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{product.notes}</p>
        </Card>
      )}

      {editing && (
        <ProductForm mode="edit" product={product} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load() }} />
      )}

      {tagOpen && (
        <TagPrintDialog
          info={{
            name: product.name,
            price,
            code: productCode(product),
            scan: product.scan_number ?? '',
            taxonomy: taxonomy || undefined,
            size: product.size ?? undefined,
          }}
          onClose={() => setTagOpen(false)}
        />
      )}

      {salesOpen && (
        <SalesAnalysisDialog productId={product.id} productName={product.name} onClose={() => setSalesOpen(false)} />
      )}

      {scrapOpen && (
        <ScrapDialog productId={product.id} productName={product.name} onClose={() => setScrapOpen(false)} onChange={load} />
      )}
    </div>
  )
}

// ---- Scrap / Loss (port of POS ScrappedInventoryUC) -------------------------
function ScrapDialog({ productId, productName, onClose, onChange }: {
  productId: string; productName: string; onClose: () => void; onChange: () => void
}) {
  const { user } = useAuth()
  const [rows, setRows] = useState<ScrappedInventory[]>([])
  const [loading, setLoading] = useState(true)
  const [qty, setQty] = useState('')
  const [reason, setReason] = useState('')
  const [by, setBy] = useState(user?.email ?? '')
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10))
  const [saving, setSaving] = useState(false)

  const reload = () => {
    setLoading(true)
    listScrappedInventory(productId).then(setRows).catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(reload, [productId])

  const save = async () => {
    const q = parseInt(qty, 10)
    if (isNaN(q) || q <= 0) { toast.error('Enter a quantity to scrap'); return }
    setSaving(true)
    try {
      await addScrappedInventory({ product_id: productId, quantity: q, reason, scrapped_by: by, scrapped_on: new Date(on).toISOString() })
      toast.success(`Scrapped ${q} — on-hand reduced`)
      setQty(''); setReason('')
      reload(); onChange()
    } catch (e) { console.error(e); toast.error('Failed to record scrap') } finally { setSaving(false) }
  }

  const remove = async (row: ScrappedInventory) => {
    if (!window.confirm('Delete this scrap record and restore the quantity to on-hand?')) return
    try { await deleteScrappedInventory(row); toast.success('Scrap record removed; quantity restored'); reload(); onChange() }
    catch { toast.error('Failed to delete scrap record') }
  }

  const totalScrapped = rows.reduce((s, r) => s + Number(r.quantity ?? 0), 0)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-lg">Scrap / Loss</h3>
            <p className="text-sm text-gray-500 mt-0.5">{productName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-gray-500">Quantity</label>
              <input type="number" min={1} value={qty} onChange={e => setQty(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Date</label>
              <input type="date" value={on} onChange={e => setOn(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Scrapped By</label>
              <input value={by} onChange={e => setBy(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div className="col-span-2 md:col-span-4">
              <label className="text-xs font-medium text-gray-500">Reason</label>
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Damaged, dead, lost…"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Recording scrap reduces the item's on-hand quantity.</span>
            <Button onClick={save} disabled={saving}><PackageMinus size={15} /> {saving ? 'Saving…' : 'Record Scrap'}</Button>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-700 text-sm">History</h4>
              <span className="text-xs text-gray-500">Total scrapped: <b>{totalScrapped}</b></span>
            </div>
            {loading ? <Spinner label="Loading…" /> : rows.length === 0 ? (
              <p className="text-center text-gray-400 py-6 text-sm">No scrap records for this item.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-right font-semibold px-3 py-2">Qty</th>
                    <th className="text-left font-semibold px-3 py-2">Date</th>
                    <th className="text-left font-semibold px-3 py-2">By</th>
                    <th className="text-left font-semibold px-3 py-2">Reason</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className="border-b border-gray-100">
                      <td className="px-3 py-1.5 text-right">{r.quantity}</td>
                      <td className="px-3 py-1.5">{date(r.scrapped_on)}</td>
                      <td className="px-3 py-1.5">{r.scrapped_by || '—'}</td>
                      <td className="px-3 py-1.5">{r.reason || '—'}</td>
                      <td className="px-3 py-1.5 text-right"><button onClick={() => remove(r)} className="text-gray-400 hover:text-red-600"><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </Card>
    </div>
  )
}

// ---- Sales Analysis (port of POS frmLarryReport) ----------------------------
// Qty sold per month at each distinct price point, for a chosen year.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function SalesAnalysisDialog({ productId, productName, onClose }: { productId: string; productName: string; onClose: () => void }) {
  const thisYear = new Date().getFullYear()
  const [year, setYear] = useState(thisYear)
  const [rows, setRows] = useState<ProductPriceBreakdownRow[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    productPriceBreakdown(productId, year)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [productId, year])

  // Distinct prices (columns), sorted ascending.
  const prices = [...new Set(rows.map(r => r.price))].sort((a, b) => a - b)
  const byMonthPrice = new Map<string, number>()
  for (const r of rows) byMonthPrice.set(`${r.month}|${r.price}`, r.qty)
  const monthTotal = (m: number) => rows.filter(r => r.month === m).reduce((s, r) => s + r.qty, 0)
  const priceTotalQty = (p: number) => rows.filter(r => r.price === p).reduce((s, r) => s + r.qty, 0)
  const totalQty = rows.reduce((s, r) => s + r.qty, 0)
  const totalAmount = rows.reduce((s, r) => s + r.qty * r.price, 0)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-lg">Sales Analysis</h3>
            <p className="text-sm text-gray-500 mt-0.5">{productName} — qty sold by price &amp; month</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm">
              {Array.from({ length: 8 }, (_, i) => thisYear - i).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
        </div>
        <div className="p-5 overflow-auto flex-1">
          {loading ? <Spinner label="Loading sales…" /> : rows.length === 0 ? (
            <p className="text-center text-gray-400 py-10">No completed sales of this item in {year}.</p>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-left font-semibold px-3 py-2 border border-gray-200">Month</th>
                  {prices.map(p => <th key={p} className="text-right font-semibold px-3 py-2 border border-gray-200">{currency(p)}</th>)}
                  <th className="text-right font-semibold px-3 py-2 border border-gray-200 bg-gray-100">Total</th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((mn, i) => {
                  const m = i + 1
                  const mt = monthTotal(m)
                  return (
                    <tr key={m} className={mt === 0 ? 'text-gray-300' : ''}>
                      <td className="px-3 py-1.5 border border-gray-200 font-medium">{mn}</td>
                      {prices.map(p => {
                        const q = byMonthPrice.get(`${m}|${p}`) ?? 0
                        return <td key={p} className="px-3 py-1.5 border border-gray-200 text-right">{q || ''}</td>
                      })}
                      <td className="px-3 py-1.5 border border-gray-200 text-right font-semibold bg-gray-50">{mt || ''}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold bg-gray-100">
                  <td className="px-3 py-2 border border-gray-200">Total Qty</td>
                  {prices.map(p => <td key={p} className="px-3 py-2 border border-gray-200 text-right">{priceTotalQty(p)}</td>)}
                  <td className="px-3 py-2 border border-gray-200 text-right">{totalQty}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            <span className="mr-4">Total Qty Sold: <b className="text-gray-900">{totalQty}</b></span>
            <span>Total Amount: <b className="text-gray-900">{currency(totalAmount)}</b></span>
          </div>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </Card>
    </div>
  )
}

// ---- Tag printing (legacy InventoryEditView Small/Large + "Qty To Print") -----

function TagPrintDialog({ info, onClose }: {
  info: { name: string; price: number; code: string; scan: string; taxonomy?: string; size?: string }
  onClose: () => void
}) {
  const [size, setSize] = useState<TagSize>('small')
  const [qty, setQty] = useState('1')
  const hasScan = /\S/.test(info.scan)

  const print = () => {
    printProductTags(info, size, parseInt(qty, 10) || 1)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Print Tags</h3>
          <p className="text-sm text-gray-500 mt-0.5">{info.name}</p>
        </div>
        <div className="p-5 space-y-4">
          {!hasScan && (
            <p className="text-sm text-red-600">This item has no scan number — assign one before printing a scannable tag.</p>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label size</label>
            <div className="flex gap-2">
              {(['small', 'large'] as TagSize[]).map(s => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border capitalize ${size === s ? 'bg-green-700 text-white border-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={e => setQty(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={print} disabled={!hasScan}><Tag size={16} /> Print</Button>
        </div>
      </Card>
    </div>
  )
}
